import { useMemo } from "react";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

// Discreet country-code selector + number. Italy (+39) is the default.
export const COUNTRY_CODES = [
  { code: "IT", dial: "+39", flag: "🇮🇹", name: "Italia" },
  { code: "RO", dial: "+40", flag: "🇷🇴", name: "România" },
  { code: "FR", dial: "+33", flag: "🇫🇷", name: "France" },
  { code: "DE", dial: "+49", flag: "🇩🇪", name: "Deutschland" },
  { code: "ES", dial: "+34", flag: "🇪🇸", name: "España" },
  { code: "GB", dial: "+44", flag: "🇬🇧", name: "United Kingdom" },
  { code: "BE", dial: "+32", flag: "🇧🇪", name: "Belgique" },
  { code: "NL", dial: "+31", flag: "🇳🇱", name: "Nederland" },
  { code: "AT", dial: "+43", flag: "🇦🇹", name: "Österreich" },
  { code: "CH", dial: "+41", flag: "🇨🇭", name: "Schweiz" },
  { code: "PT", dial: "+351", flag: "🇵🇹", name: "Portugal" },
  { code: "PL", dial: "+48", flag: "🇵🇱", name: "Polska" },
  { code: "CZ", dial: "+420", flag: "🇨🇿", name: "Česko" },
  { code: "HU", dial: "+36", flag: "🇭🇺", name: "Magyarország" },
  { code: "BG", dial: "+359", flag: "🇧🇬", name: "България" },
  { code: "GR", dial: "+30", flag: "🇬🇷", name: "Ελλάδα" },
  { code: "MD", dial: "+373", flag: "🇲🇩", name: "Moldova" },
  { code: "UA", dial: "+380", flag: "🇺🇦", name: "Україна" },
  { code: "RS", dial: "+381", flag: "🇷🇸", name: "Srbija" },
  { code: "TR", dial: "+90", flag: "🇹🇷", name: "Türkiye" },
  { code: "US", dial: "+1", flag: "🇺🇸", name: "USA / Canada" },
  { code: "MA", dial: "+212", flag: "🇲🇦", name: "Maroc" },
  { code: "TN", dial: "+216", flag: "🇹🇳", name: "Tunisie" },
];

const DIALS = COUNTRY_CODES.map((c) => c.dial).sort((a, b) => b.length - a.length);

function splitPhone(value) {
  const v = (value || "").trim();
  for (const dial of DIALS) {
    if (v.startsWith(dial)) return { dial, number: v.slice(dial.length).trim() };
  }
  return { dial: "+39", number: v.replace(/^\+/, "") };
}

export function PhoneInput({ value, onChange, testid = "phone" }) {
  const { dial, number } = useMemo(() => splitPhone(value), [value]);

  const emit = (newDial, newNumber) => {
    const clean = (newNumber || "").replace(/[^\d\s]/g, "").trim();
    onChange(clean ? `${newDial} ${clean}` : newDial);
  };

  const current = COUNTRY_CODES.find((c) => c.dial === dial) || COUNTRY_CODES[0];

  return (
    <div className="flex gap-2">
      <Select value={current.dial} onValueChange={(v) => emit(v, number)}>
        <SelectTrigger className="w-[92px] shrink-0" data-testid={`${testid}-dial`}>
          <SelectValue>
            <span className="flex items-center gap-1"><span>{current.flag}</span><span className="text-sm">{current.dial}</span></span>
          </SelectValue>
        </SelectTrigger>
        <SelectContent className="max-h-72">
          {COUNTRY_CODES.map((c) => (
            <SelectItem key={c.code} value={c.dial}>
              <span className="flex items-center gap-2"><span>{c.flag}</span><span className="text-sm font-medium">{c.dial}</span><span className="text-xs text-slate-400">{c.name}</span></span>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Input
        type="tel"
        value={number}
        onChange={(e) => emit(current.dial, e.target.value)}
        placeholder="333 123 4567"
        data-testid={`${testid}-number`}
      />
    </div>
  );
}
